// src/pages/UnauthorizedPage.jsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ArrowLeft, Home, LogOut, AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button/Button';

/**
 * Unauthorized access page component
 * Displays when users try to access routes they don't have permission for
 */
export default function UnauthorizedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, userDisplayName, userRoleDisplayName } = useAuth();

  // Get the attempted route from location state
  const attemptedRoute = location.state?.from?.pathname || 'this page';

  const handleGoBack = () => {
    // Go back to previous page or dashboard
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if logout fails
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md ">
        {/* Main Card */}
        <div
          className="bg-white rounded-2xl shadow-xl p-8 text-center"
          style={{
            boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          {/* Icon */}
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            You don't have permission to access {attemptedRoute}. 
            Please contact your administrator if you believe this is an error.
          </p>

          {/* User Info */}
          {user && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {userDisplayName}
                  </div>
                  <div className="text-xs text-gray-500">
                    Role: {userRoleDisplayName}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <AlertTriangle className="w-3 h-3" />
                <span>Your current role doesn't have access to this resource</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleGoBack}
              variant="primary"
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>

            <Button
              onClick={handleGoHome}
              variant="secondary"
              className="w-full"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>

            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full text-gray-600 hover:text-red-600 hover:border-red-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact your system administrator or{' '}
            <button
              onClick={() => window.location.href = 'mailto:support@cabtrack.com'}
              className="text-indigo-600 hover:text-indigo-800 underline"
            >
              support team
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Simplified unauthorized component for inline use
 */
export const UnauthorizedMessage = ({ 
  message = "You don't have permission to view this content.",
  showActions = true,
  className = ""
}) => {
  const navigate = useNavigate();

  return (
    <div className={`text-center py-12 px-4 ${className}`}>
      <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <Shield className="w-6 h-6 text-red-600" />
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Access Restricted
      </h3>
      
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        {message}
      </p>

      {showActions && (
        <div className="flex gap-3 justify-center">
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Go Back
          </Button>
          
          <Button
            onClick={() => navigate('/dashboard')}
            variant="primary"
            size="sm"
          >
            <Home className="w-4 h-4 mr-1" />
            Dashboard
          </Button>
        </div>
      )}
    </div>
  );
};
